/**
 * Test Mode Detection Utility
 * 
 * Handles test mode detection and localStorage persistence
 */

export const TEST_MODE_KEY = 'test_mode';

/**
 * Check if test mode should be active
 */
export function detectTestMode(): boolean {
  if (typeof window === 'undefined') return false;
  
  const currentUrl = window.location.href;
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
  
  const isTestMode = currentUrl.includes('test=true') || 
                   window.location.search.includes('test=true') ||
                   window.location.hash.includes('test=true') ||
                   urlParams.get('test') === 'true' ||
                   hashParams.get('test') === 'true' ||
                   localStorage.getItem(TEST_MODE_KEY) === 'true' ||
                   localStorage.getItem('test') === 'true'; // Also check the 'test' key
  
  return isTestMode;
}

/**
 * Initialize test mode based on URL and persist to localStorage
 */
export function initializeTestMode(): boolean {
  if (typeof window === 'undefined') return false;
  
  const currentUrl = window.location.href;
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.replace('#', ''));
  
  // Check if test=true is in URL
  const urlHasTest = currentUrl.includes('test=true') || 
                   window.location.search.includes('test=true') ||
                   window.location.hash.includes('test=true') ||
                   urlParams.get('test') === 'true' ||
                   hashParams.get('test') === 'true';
  
  // Check if test=false is in URL (to disable test mode)
  const urlHasTestFalse = currentUrl.includes('test=false') || 
                        window.location.hash.includes('test=false') ||
                        urlParams.get('test') === 'false' ||
                        hashParams.get('test') === 'false';
  
  if (urlHasTest) {
    localStorage.setItem(TEST_MODE_KEY, 'true');
    localStorage.setItem('test', 'true'); // Also set the 'test' key
    console.log('[Test Mode] 🚫 Enabled - Analytics tracking disabled');
    return true;
  } else if (urlHasTestFalse) {
    localStorage.removeItem(TEST_MODE_KEY);
    localStorage.removeItem('test'); // Also remove the 'test' key
    console.log('[Test Mode] ✅ Disabled - Analytics tracking enabled');
    return false;
  }
  
  // Check existing localStorage settings (both keys)
  const isTestMode = localStorage.getItem(TEST_MODE_KEY) === 'true' || 
                     localStorage.getItem('test') === 'true';
  if (isTestMode) {
    console.log('[Test Mode] 🚫 Enabled (from localStorage) - Analytics tracking disabled');
  }
  
  return isTestMode;
}

/**
 * Enable test mode
 */
export function enableTestMode(): void {
  localStorage.setItem(TEST_MODE_KEY, 'true');
  console.log('[Test Mode] 🚫 Manually enabled - Analytics tracking disabled');
}

/**
 * Disable test mode
 */
export function disableTestMode(): void {
  localStorage.removeItem(TEST_MODE_KEY);
  console.log('[Test Mode] ✅ Manually disabled - Analytics tracking enabled');
}